import React, { useEffect, useMemo, useState } from "react";
import { loadsResource } from "../../helpers/configRoutes/backend_helper";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Componente de Loads
 * Mobile-first / operador-proof
 */
const LoadsComponent = () => {
    const [loadsData, setLoadsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const isLoadCompleted = (load) => load.status_id === 29
        || (load.stops?.length > 0 && load.stops.every((stop) => stop.status_operator_id === 33));

    const getNextActionLabel = (stop, loadStatusName) => {
        if (!stop) {
            return loadStatusName || "Load ready";
        }

        if (stop.can?.arrive) return "Arrive at stop";
        if (stop.can?.depart) return "Depart from stop";
        if (stop.can?.complete) return "Complete stop";

        return stop.statusOperator?.name || stop.status?.name || loadStatusName || "In progress";
    };

    useEffect(() => {
        const fetchLoads = async () => {
            try {
                const response = await loadsResource("get", { params: { perPage: 100 } });
                const orderedLoads = [...(response.loads || [])].sort((left, right) => Number(right.id) - Number(left.id));
                setLoadsData(orderedLoads);
            } catch (e) {
                console.error("Error loading loads", e);
            } finally {
                setLoading(false);
            }
        };

        fetchLoads();
    }, [location.key, location.state?.refreshedAt]);

    const { activeLoads, completedLoads } = useMemo(() => {
        const active = [];
        const completed = [];

        loadsData.forEach((load) => {
            if (isLoadCompleted(load)) {
                completed.push(load);
                return;
            }

            active.push(load);
        });

        return {
            activeLoads: active,
            completedLoads: completed,
        };
    }, [loadsData]);

    const renderLoadCard = (load, completedLoad = false) => {
        const nextStop = completedLoad
            ? null
            : load.stops?.find(
                (stop) => stop.can?.arrive || stop.can?.depart || stop.can?.complete
            ) || load.first_stop;

        const completedStops =
            load.stops?.filter((stop) => stop.status_operator_id === 33).length || 0;

        const nextActionLabel = completedLoad
            ? (load.status?.name || "Finished")
            : getNextActionLabel(nextStop, load.status?.name);

        const stopIndex = nextStop
            ? (load.stops?.findIndex((stop) => stop.id === nextStop?.id) + 1 || 1)
            : (load.stops?.length || 0);

        const isJustCompleted = Number(location.state?.completedLoadId) === Number(load.id);

        return (
            <div
                key={load.id}
                onClick={() => navigate(`/active-load/${load.id}`)}
                className={`rounded-3xl shadow-soft p-5 flex flex-col gap-4 active:scale-[0.98] transition border ${completedLoad
                    ? 'bg-white border-cs-success/20'
                    : 'bg-cs-bg border-transparent'
                    }`}
            >
                <div className="text-center">
                    <p className="text-lg font-bold text-cs-dark line-clamp-1">
                        {load.first_stop?.name}
                    </p>
                    <p className="text-xs text-cs-muted">to</p>
                    <p className="text-lg font-bold text-cs-dark line-clamp-1">
                        {load.last_stop?.name}
                    </p>
                </div>

                <div className={`${completedLoad ? 'bg-cs-success/10 border border-cs-success/20' : 'bg-cs-primary-soft'} rounded-2xl p-3 text-center`}>
                    <p className={`text-xs font-semibold ${completedLoad ? 'text-cs-success' : 'text-cs-primary-dark'}`}>
                        {completedLoad ? 'LOAD STATUS' : 'NEXT STOP'}
                    </p>
                    <p className="text-lg font-bold text-cs-dark line-clamp-1">
                        {completedLoad ? 'Load completed' : (nextStop?.name || 'Route in progress')}
                    </p>
                    <p className={`text-[11px] font-extrabold uppercase tracking-wide mt-1 ${completedLoad ? 'text-cs-success' : 'text-cs-primary'}`}>
                        {nextActionLabel}
                    </p>
                    <p className="text-[11px] text-cs-muted mt-2">
                        {completedLoad
                            ? 'All stops are done. Open this load to review the finished trip.'
                            : `Open this load to continue with stop ${stopIndex}.`}
                    </p>
                    {isJustCompleted && (
                        <p className="text-[10px] font-black text-cs-success uppercase tracking-wide mt-2">
                            Just completed
                        </p>
                    )}
                </div>

                <div className="flex justify-between text-sm text-cs-dark">
                    <span>Pickup</span>
                    <span className="font-semibold">
                        {load.pick_up_date?.slice(11, 16) || "--:--"}
                    </span>
                </div>

                <div className="flex justify-between text-sm text-cs-dark">
                    <span>Delivery</span>
                    <span className="font-semibold">
                        {load.delivery_date?.slice(11, 16) || "--:--"}
                    </span>
                </div>

                <div className="flex justify-between text-sm text-cs-muted border-t border-cs-border pt-3">
                    <span>{load.practical_miles} mi</span>
                    <span>${load.charges}</span>
                    <span>
                        Stop {stopIndex}/{load.stops?.length}
                    </span>
                </div>

                <div className="flex items-center justify-between text-[11px] font-semibold text-cs-muted bg-cs-bg-alt rounded-2xl px-3 py-2">
                    <span>{completedStops}/{load.stops?.length || 0} stops completed</span>
                    <span className={completedLoad ? 'text-cs-success' : 'text-cs-primary'}>
                        {completedLoad ? 'Tap to review completed load' : 'Tap to review next stop'}
                    </span>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cs-bg-alt">
                <p className="text-cs-muted text-base">Loading loads…</p>
            </div>
        );
    }

    if (!loadsData.length) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-cs-bg-alt px-6 text-center">
                <p className="text-lg font-semibold text-cs-dark mb-2">
                    No loads assigned
                </p>
                <p className="text-sm text-cs-muted">
                    When you have a load, it will appear here.
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cs-bg-alt px-4 pt-4 pb-28">
            <h1 className="text-xl font-bold text-cs-dark mb-4 text-center">
                My Loads
            </h1>

            <div className="flex flex-col gap-8">
                <section>
                    <div className="flex items-center justify-between mb-3 px-1">
                        <h2 className="text-[11px] font-bold text-cs-muted uppercase tracking-[0.2em]">
                            Active Loads
                        </h2>
                        <span className="text-[10px] font-bold text-cs-primary bg-cs-primary-soft px-2.5 py-1 rounded-full">
                            {activeLoads.length}
                        </span>
                    </div>

                    {activeLoads.length > 0 ? (
                        <div className="flex flex-col gap-5">
                            {activeLoads.map((load) => renderLoadCard(load, false))}
                        </div>
                    ) : (
                        <div className="bg-white rounded-3xl border border-cs-border p-6 text-center text-sm text-cs-muted">
                            No active loads right now.
                        </div>
                    )}
                </section>

                <section>
                    <div className="flex items-center justify-between mb-3 px-1">
                        <h2 className="text-[11px] font-bold text-cs-muted uppercase tracking-[0.2em]">
                            Finished Loads
                        </h2>
                        <span className="text-[10px] font-bold text-cs-success bg-cs-success/10 px-2.5 py-1 rounded-full">
                            {completedLoads.length}
                        </span>
                    </div>

                    {completedLoads.length > 0 ? (
                        <div className="flex flex-col gap-5">
                            {completedLoads.map((load) => renderLoadCard(load, true))}
                        </div>
                    ) : (
                        <div className="bg-white rounded-3xl border border-cs-border p-6 text-center text-sm text-cs-muted">
                            No finished loads yet.
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default LoadsComponent;
